/*
 * mjukt-reveal.js
 * Global, subtle scroll-reveal for main content sections.
 *
 * Strategy:
 *  - The inline <head> snippet (in theme.liquid) has already added `js-reveal`
 *    to <html> when motion is allowed and we are not in the theme editor, plus
 *    a failsafe timeout that removes it if this file never signals `reveal-ready`.
 *  - Here we mark `reveal-ready` (cancelling the failsafe) and use a single
 *    IntersectionObserver to add `is-revealed` to each top-level main section
 *    as it crosses ~13% visibility. Sections already in the initial viewport
 *    reveal immediately with a small stagger so first paint feels alive.
 *  - Transform/opacity only. No scroll listeners. One-shot (unobserve on reveal).
 *  - Editor + reduced-motion + JS-failure all resolve to fully visible content.
 */
(function () {
  'use strict';

  var docEl = document.documentElement;
  var SECTION_SELECTOR = '#MainContent > .shopify-section';

  // Known grid/card containers whose direct children get a gentle cascade.
  var STAGGER_CONTAINERS = [
    '.product-features__grid',
    '.value-props__grid',
    '.how-it-works__steps',
    '.mjukt-impact-stats__grid',
    '.mjukt-shop__grid',
    '.faq__list'
  ];
  var STAGGER_CAP = 6; // max order => 480ms at 80ms steps

  // Editor: never hide anything. Reveal instantly and stop.
  if (window.Shopify && window.Shopify.designMode) {
    revealAllInstant();
    // Re-reveal sections the editor swaps in.
    document.addEventListener('shopify:section:load', function (e) {
      var s = e.target;
      if (s && s.classList && s.classList.contains('shopify-section')) {
        markRevealed(s, true);
      }
    });
    return;
  }

  // Reduced motion: unhide and stop (defensive; the inline snippet also skips).
  var reduceMQ = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)');
  if (reduceMQ && reduceMQ.matches) {
    docEl.classList.remove('js-reveal');
    docEl.classList.add('reveal-ready');
    return;
  }

  // No IntersectionObserver support -> reveal everything, stay safe.
  if (!('IntersectionObserver' in window)) {
    docEl.classList.remove('js-reveal');
    docEl.classList.add('reveal-ready');
    return;
  }

  // Signal success so the head failsafe leaves the gate class in place.
  docEl.classList.add('reveal-ready');

  var observer = new IntersectionObserver(onIntersect, {
    root: null,
    rootMargin: '0px 0px -5% 0px',
    /* 0 catches sections taller than the viewport (13% of them can never be
       visible, so a 0.13-only threshold would leave them hidden forever). */
    threshold: [0, 0.13]
  });

  function onIntersect(entries) {
    entries.forEach(function (entry) {
      if (!entry.isIntersecting) return;
      var tall = entry.target.offsetHeight > window.innerHeight * 0.85;
      if (tall || entry.intersectionRatio >= 0.13) {
        markRevealed(entry.target, false);
        observer.unobserve(entry.target);
      }
    });
  }

  function prepStagger(section) {
    for (var i = 0; i < STAGGER_CONTAINERS.length; i++) {
      var container = section.querySelector(STAGGER_CONTAINERS[i]);
      if (!container) continue;
      var children = container.children;
      if (!children || children.length < 2) continue;
      section.classList.add('reveal-stagger');
      for (var c = 0; c < children.length; c++) {
        var order = c < STAGGER_CAP ? c : STAGGER_CAP;
        children[c].classList.add('reveal-child');
        children[c].style.setProperty('--reveal-order', order);
      }
      break; // one stagger container per section is enough
    }
  }

  function markRevealed(section, instant) {
    if (!section || section.classList.contains('is-revealed')) return;
    section.classList.add('is-revealed');
    // Retire the will-change hint after the transition settles.
    window.setTimeout(function () {
      section.classList.add('reveal-done');
    }, instant ? 0 : 750);
  }

  function observeSection(section) {
    if (!section || section.getAttribute('data-reveal-init') === '1') return;
    section.setAttribute('data-reveal-init', '1');
    prepStagger(section);
    observer.observe(section);
  }

  function initAll(root) {
    var scope = root || document;
    var sections = scope.querySelectorAll(SECTION_SELECTOR);
    for (var i = 0; i < sections.length; i++) {
      observeSection(sections[i]);
    }
  }

  function revealAllInstant() {
    var sections = document.querySelectorAll(SECTION_SELECTOR);
    for (var i = 0; i < sections.length; i++) {
      markRevealed(sections[i], true);
    }
  }

  function start() {
    initAll(document);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }

  // Theme editor may not be in designMode for all events; re-scan on load.
  document.addEventListener('shopify:section:load', function (e) {
    var s = e.target;
    if (s && s.classList && s.classList.contains('shopify-section')) {
      // Only top-level main sections participate.
      if (s.parentElement && s.parentElement.id === 'MainContent') {
        observeSection(s);
      }
    }
  });
})();
